import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import * as secretsApi from './secrets.api';

import type { CreateSecretRequest, SecretListQuery, UpdateSecretRequest } from '@utazzo/shared';

const QK = {
  list: (query: SecretListQuery) => ['secrets', 'list', query] as const,
  detail: (id: string) => ['secrets', 'detail', id] as const,
};

export const useSecretsList = (query: SecretListQuery) =>
  useQuery({
    queryKey: QK.list(query),
    queryFn: () => secretsApi.listSecrets(query),
    staleTime: 30_000,
  });

export const useCreateSecret = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateSecretRequest) => secretsApi.createSecret(payload),
    onSuccess: (s) => {
      qc.invalidateQueries({ queryKey: ['secrets', 'list'] });
      toast.success(`Added secret ${s.key}`);
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to add secret'),
  });
};

export const useUpdateSecret = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateSecretRequest }) =>
      secretsApi.updateSecret(id, payload),
    onSuccess: (s) => {
      qc.invalidateQueries({ queryKey: ['secrets', 'list'] });
      qc.invalidateQueries({ queryKey: QK.detail(s.id) });
      toast.success('Secret updated');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to update secret'),
  });
};

export const useDeleteSecret = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => secretsApi.deleteSecret(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['secrets', 'list'] });
      toast.success('Secret deleted');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete secret'),
  });
};

/** Reveal is intentionally not cached - each reveal is a fresh, audited fetch. */
export const useRevealSecret = () =>
  useMutation({
    mutationFn: (id: string) => secretsApi.revealSecret(id),
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to reveal secret'),
  });

const extractMessage = (err: unknown): string | null => {
  if (err && typeof err === 'object') {
    const e = err as { message?: unknown; details?: { message?: unknown } };
    if (e.details && typeof e.details.message === 'string') return e.details.message;
    if (typeof e.message === 'string') return e.message;
  }
  if (err instanceof Error) return err.message;
  return null;
};
